import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtEUR, fmtDate, INVOICE_STATUS } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/Layout";
import { DocumentDialog } from "../components/DocumentDialog";
import { MonthlyInvoiceDialog } from "../components/MonthlyInvoiceDialog";
import { InvoiceTrashDialog } from "../components/InvoiceTrashDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Download, Pencil, Trash2, Search, FileText, CalendarRange, CheckCircle2, CalendarPlus, Archive } from "lucide-react";
import { generateDocumentPDF } from "../lib/pdf";
import { addInvoiceToCalendar } from "../lib/ics";
import { toast } from "sonner";

export default function Invoices() {
  const qc = useQueryClient();
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: api.listInvoices });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });

  const [open, setOpen] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [rows, setRows] = useState({});
  const [view, setView] = useState("active");

  const refresh = () => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const archived = invoices.filter((i) => i.combined_into);
  const active = invoices.filter((i) => !i.combined_into);
  const filtered = (view === "archives" ? archived : active).filter((i) =>
    (i.number + " " + (i.client?.name || "")).toLowerCase().includes(search.toLowerCase())
  );

  const selectedIds = filtered.filter((i) => rows[i.id]).map((i) => i.id);
  const allChecked = filtered.length > 0 && filtered.every((i) => rows[i.id]);
  const toggleRow = (id) => setRows((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    if (allChecked) return setRows({});
    const n = {};
    filtered.forEach((i) => { n[i.id] = true; });
    setRows(n);
  };
  const markPaid = async () => {
    try {
      await api.bulkInvoiceStatus(selectedIds, "payee");
      setRows({});
      refresh();
      toast.success(`${selectedIds.length} facture(s) marquée(s) payée(s)`);
    } catch {
      toast.error("Erreur lors de la mise à jour des factures");
    }
  };

  const handleDownload = (inv) => {
    generateDocumentPDF(inv, settings || {}, "invoice");
    toast.success(`PDF ${inv.number} téléchargé`);
  };

  const confirmDelete = async () => {
    try { await api.deleteInvoice(toDelete.id); } catch { return toast.error("Erreur lors de la suppression"); }
    setToDelete(null);
    refresh();
    qc.invalidateQueries({ queryKey: ["invoice-trash"] });
    toast.success("Facture déplacée dans la corbeille");
  };

  return (
    <div>
      <PageHeader
        title="Factures"
        subtitle={`${active.length} facture(s) · ${archived.length} archivée(s)`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setTrashOpen(true)} className="border-slate-300" data-testid="invoice-trash-btn">
              <Archive className="h-4 w-4 mr-1.5" /> Corbeille
            </Button>
            <Button variant="outline" onClick={() => setMonthlyOpen(true)} className="border-slate-300" data-testid="monthly-invoice-btn">
              <CalendarRange className="h-4 w-4 mr-1.5" /> Regrouper des factures
            </Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-invoice-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Créer une facture
            </Button>
          </div>
        }
      />

      <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="invoice-view-tabs">
        {[["active", `Factures (${active.length})`], ["archives", `Archives (${archived.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => { setView(k); setRows({}); }} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`invoice-tab-${k}`}>{label}</button>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9 bg-white" placeholder="Rechercher une facture..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="invoice-search-input" />
      </div>

      {selectedIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-xl bg-slate-900 text-white px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm">{selectedIds.length} facture(s) sélectionnée(s)</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setRows({})}>Annuler</Button>
            <Button size="sm" className="bg-[#C5A059] hover:bg-[#b38f48] text-slate-900" onClick={markPaid} data-testid="bulk-mark-paid-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Marquer payée</Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 w-10"><input type="checkbox" className="h-4 w-4 accent-[#C5A059]" checked={allChecked} onChange={toggleAll} data-testid="select-all-invoices" /></th>
                <th className="px-4 py-3 font-semibold">N° Facture</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Émission</th>
                <th className="px-4 py-3 font-semibold">Échéance</th>
                <th className="px-4 py-3 font-semibold text-right">HT</th>
                <th className="px-4 py-3 font-semibold text-right">TTC</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? filtered.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`invoice-row-${inv.number}`}>
                  <td className="px-4 py-3"><input type="checkbox" className="h-4 w-4 accent-[#C5A059]" checked={!!rows[inv.id]} onChange={() => toggleRow(inv.id)} data-testid={`select-invoice-${inv.number}`} /></td>
                  <td className="px-4 py-3 font-mono text-slate-700">{inv.number}{inv.combined_into && <span className="ml-1.5 text-[10px] text-slate-400" title={`Regroupée dans ${inv.combined_into}`}>↳ {inv.combined_into}</span>}</td>
                  <td className="px-4 py-3 font-medium">{inv.client?.name}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(inv.date_emission)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(inv.date_echeance)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtEUR(inv.total_ht)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmtEUR(inv.total_ttc)}</td>
                  <td className="px-4 py-3"><StatusBadge meta={INVOICE_STATUS[inv.status]} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleDownload(inv)} title="Télécharger PDF" data-testid={`download-pdf-${inv.number}`}><Download className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { addInvoiceToCalendar(inv) ? toast.success(`Échéance de ${inv.number} ajoutée au calendrier`) : toast.error("Aucune date d'échéance pour cette facture"); }} title="Ajouter l'échéance au calendrier (iPhone)" data-testid={`calendar-invoice-${inv.number}`}><CalendarPlus className="h-4 w-4 text-[#C5A059]" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(inv); setOpen(true); }} title="Modifier" data-testid={`edit-invoice-${inv.number}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setToDelete(inv)} title="Supprimer" data-testid={`delete-invoice-${inv.number}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />{view === "archives" ? "Aucune facture archivée" : "Aucune facture"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DocumentDialog
        open={open} onOpenChange={setOpen} type="invoice" clients={clients}
        editingDoc={editing} defaultTva={settings?.default_tva || 10}
        apiCreate={api.createInvoice} apiUpdate={api.updateInvoice} onSaved={refresh}
      />

      <MonthlyInvoiceDialog
        open={monthlyOpen} onOpenChange={setMonthlyOpen}
        clients={clients} invoices={invoices} onSaved={refresh}
      />

      <InvoiceTrashDialog open={trashOpen} onOpenChange={setTrashOpen} onRestored={refresh} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la facture ?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.number} sera déplacée dans la corbeille. Vous pourrez la restaurer à tout moment.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-invoice">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
